import React, { useState, useMemo } from "react";
import { confirmAction, successAlert, errorAlert } from "../common/Swal";
import {
  revertTransactionBySection,
  settleTransaction,
} from "../../services/transactionService";
import { getAmountInCurrencyFormat, limitNumber } from "../../utils/number";

/* ─────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────── */
const showValue = (val) =>
  val !== undefined && val !== null && val !== 0 ? limitNumber(val) : "—";

const fmt = (dateStr) => {
  try {
    return new Date(dateStr).toLocaleDateString("en-GB");
  } catch {
    return "—";
  }
};

const sum = (arr, fn) => (arr || []).reduce((acc, i) => acc + (fn(i) || 0), 0);

/* ─────────────────────────────────────────────
   CONSTANTS
───────────────────────────────────────────── */
const STATUS_CLASS = {
  pending: { bg: "#fef3c7", color: "#92400e" },
  completed: { bg: "#d1fae5", color: "#065f46" },
  rejected: { bg: "#fee2e2", color: "#991b1b" },
};

const COLUMNS = [
  { key: "sno", label: "S.No" },
  { key: "party", label: "Party" },
  { key: "description", label: "Description" },
  { key: "weight", label: "Weight" },
  { key: "rate", label: "Rate" },
  { key: "diff", label: "Diff" },
  { key: "amount", label: "Amount" },
  { key: "status", label: "Status" },
  { key: "postDate", label: "Post Date" },
  { key: "date", label: "Date" },
  { key: "time", label: "Time" },
];

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function TradeTable({
  data = { buy: [], sell: [] },
  loading = false,
  refetch,
}) {
  const [rescheduleId, setRescheduleId] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [showActions, setShowActions] = useState(true);

  const buyRows = data.buy || [];
  const sellRows = data.sell || [];

  /* Totals */
  const buyTotals = {
    weight: sum(buyRows, (i) => i.weight),
    rate: sum(buyRows, (i) => i.rate),
    amount: sum(buyRows, (i) => i.amount),
  };
  const sellTotals = {
    weight: sum(sellRows, (i) => i.weight),
    rate: sum(sellRows, (i) => i.rate),
    amount: sum(sellRows, (i) => i.amount),
  };
  const balance = {
    weight: buyTotals.weight - sellTotals.weight,
    rate: buyTotals.rate - sellTotals.rate,
    amount: buyTotals.amount - sellTotals.amount,
  };

  /* Stats */
  const stats = useMemo(() => {
    const parties = new Set([
      ...buyRows.map((r) => r.partyName),
      ...sellRows.map((r) => r.partyName),
    ]);
    return {
      buyCount: buyRows.length,
      sellCount: sellRows.length,
      partyCount: parties.size,
      weightDir: balance.weight >= 0 ? "receivable" : "payable",
      amountDir: balance.amount >= 0 ? "receivable" : "payable",
    };
  }, [buyRows, sellRows]);

  /* Paired rows */
  const maxLen = Math.max(buyRows.length, sellRows.length);
  const pairs = Array.from({ length: maxLen }, (_, i) => ({
    buy: buyRows[i] || null,
    sell: sellRows[i] || null,
    idx: i,
  }));

  /* Action handler */
  const handleAction = async (id, payload) => {
    const msgs = {
      complete: [
        "Mark this transaction as COMPLETE?",
        "Transaction marked as completed ✅",
      ],
      reject: [
        "Are you sure you want to REJECT this?",
        "Transaction rejected ❌",
      ],
      reschedule: ["Confirm reschedule?", "Transaction rescheduled 🔄"],
      deleteTx: [
        "Are you sure you want to DELETE this?",
        "Transaction deleted 🗑️",
      ],
    };
    const [message, successMsg] = msgs[payload.action] || ["Confirm?", "Done"];
    const confirmed = await confirmAction(message);
    if (!confirmed) return;

    try {
      if (payload.action === "deleteTx") {
        const response = await revertTransactionBySection(id, payload.section);
        if (!response.success) {
          throw new Error(response.message || "Failed to delete transaction");
        }
      } else {
        await settleTransaction(id, payload);
      }
      setRescheduleId(null);
      setNewDate("");
      refetch();
      successAlert(successMsg);
    } catch (err) {
      console.error(err);
      errorAlert("Action failed. Please try again.");
    }
  };

  /* Column count for colSpan */
  const colCount = COLUMNS.length;
  const actColCount = showActions ? colCount + 1 : colCount;

  return (
    <div className="container my-2">
      {/* ── STATS ── */}
      {/* <div className="lt2-stats-grid" style={{ marginBottom: 16 }}>
        <StatCard
          label="BUY Entries"
          value={stats.buyCount}
          icon="fa-arrow-down"
          color="rx"
        />
        <StatCard
          label="SELL Entries"
          value={stats.sellCount}
          icon="fa-arrow-up"
          color="gv"
        />
        <StatCard
          label="Parties"
          value={stats.partyCount}
          icon="fa-users"
          color="neutral"
        />
        <StatCard
          label="Wt Balance"
          value={`${limitNumber(Math.abs(balance.weight))} g`}
          icon="fa-weight-hanging"
          color="gold"
          direction={stats.weightDir}
        />
        <StatCard
          label="Amt Balance"
          value={limitNumber(Math.abs(balance.amount))}
          icon="fa-money-bill-wave"
          color="cash"
          direction={stats.amountDir}
        />
        <StatCard
          label="BUY Amount"
          value={limitNumber(buyTotals.amount)}
          icon="fa-arrow-trend-down"
          color="rx"
        />
        <StatCard
          label="SELL Amount"
          value={limitNumber(sellTotals.amount)}
          icon="fa-arrow-trend-up"
          color="gv"
        />
      </div> */}

      {/* ── ACTIONS TOGGLE ── */}
      {/* <div className="lt2-stats-toggle" style={{ marginBottom: 12 }}>
        <button
          className={`lt2-stats-btn ${showActions ? "active" : ""}`}
          onClick={() => setShowActions((p) => !p)}
        >
          <i className="fas fa-sliders" />
          {showActions ? "Hide Actions" : "Show Actions"}
        </button>
      </div> */}

      {/* ── TABLE ── */}
      <div className="lt2-wrap">
        <div className="lt2-scroll">
          {loading ? (
            <div className="lt2-state">
              <div className="lt2-spinner" />
              <span>Loading trades…</span>
            </div>
          ) : pairs.length === 0 ? (
            <div className="lt2-state lt2-empty">
              <i className="fas fa-inbox" />
              <span>No trade data found</span>
            </div>
          ) : (
            <table className="lt2-table">
              <thead>
                {/* ── Section header row ── */}
                <tr className="lt2-head-group">
                  <th
                    colSpan={actColCount}
                    className="lt2-section-head lt2-head-rx"
                  >
                    <i className="fas fa-arrow-down" /> BUY
                    <span className="lt2-badge">{buyRows.length}</span>
                  </th>
                  <th className="lt2-divider-col" rowSpan={2} />
                  <th
                    colSpan={actColCount}
                    className="lt2-section-head lt2-head-gv"
                  >
                    <i className="fas fa-arrow-up" /> SELL
                    <span className="lt2-badge">{sellRows.length}</span>
                  </th>
                </tr>

                {/* ── Column label row ── */}
                <tr className="lt2-head-cols">
                  {/* BUY cols */}
                  {COLUMNS.map((col) => (
                    <th key={`buy-h-${col.key}`} className="lt2-th">
                      {col.label}
                    </th>
                  ))}
                  {showActions && <th className="lt2-th no-print">Action</th>}

                  {/* SELL cols */}
                  {COLUMNS.map((col) => (
                    <th key={`sell-h-${col.key}`} className="lt2-th">
                      {col.label}
                    </th>
                  ))}
                  {showActions && <th className="lt2-th no-print">Action</th>}
                </tr>
              </thead>

              <tbody>
                {pairs.map(({ buy, sell, idx }) => (
                  <tr key={idx} className="lt2-row">
                    {/* BUY side */}
                    {buy ? (
                      <>
                        <DataCells row={buy} sno={idx + 1} />
                        {showActions && (
                          <td className="lt2-td-actions no-print">
                            <ActionCell
                              item={buy}
                              rescheduleId={rescheduleId}
                              newDate={newDate}
                              setRescheduleId={setRescheduleId}
                              setNewDate={setNewDate}
                              handleAction={handleAction}
                            />
                          </td>
                        )}
                      </>
                    ) : (
                      <td colSpan={actColCount} className="lt2-td lt2-td-empty">
                        —
                      </td>
                    )}

                    {/* Divider */}
                    <td className="lt2-divider-col" />

                    {/* SELL side */}
                    {sell ? (
                      <>
                        <DataCells row={sell} sno={idx + 1} side="gv" />
                        {showActions && (
                          <td className="lt2-td-actions no-print">
                            <ActionCell
                              item={sell}
                              rescheduleId={rescheduleId}
                              newDate={newDate}
                              setRescheduleId={setRescheduleId}
                              setNewDate={setNewDate}
                              handleAction={handleAction}
                            />
                          </td>
                        )}
                      </>
                    ) : (
                      <td colSpan={actColCount} className="lt2-td lt2-td-empty">
                        —
                      </td>
                    )}
                  </tr>
                ))}

                {/* ── TOTALS ROW ── */}
                <tr className="lt2-total-row">
                  <td colSpan={3} className="lt2-td lt2-td-total tt-label">
                    BUY TOTAL
                  </td>
                  <td className="lt2-td lt2-td-total">
                    {showValue(buyTotals.weight)}
                  </td>
                  <td className="lt2-td lt2-td-total">
                    {getAmountInCurrencyFormat(showValue(buyTotals.rate))}
                  </td>
                  <td className="lt2-td lt2-td-total">—</td>
                  <td className="lt2-td lt2-td-total">
                    {getAmountInCurrencyFormat(showValue(buyTotals.amount))}
                  </td>
                  <td
                    colSpan={showActions ? 5 : 4}
                    className="lt2-td lt2-td-total"
                  />
                  <td className="lt2-divider-col" />
                  <td colSpan={3} className="lt2-td lt2-td-total tt-label">
                    SELL TOTAL
                  </td>
                  <td className="lt2-td lt2-td-total">
                    {showValue(sellTotals.weight)}
                  </td>
                  <td className="lt2-td lt2-td-total">
                    {getAmountInCurrencyFormat(showValue(sellTotals.rate))}
                  </td>
                  <td className="lt2-td lt2-td-total">—</td>
                  <td className="lt2-td lt2-td-total">
                    {getAmountInCurrencyFormat(showValue(sellTotals.amount))}
                  </td>
                  <td
                    colSpan={showActions ? 5 : 4}
                    className="lt2-td lt2-td-total"
                  />
                </tr>

                {/* ── BALANCE ROW ── */}
                <tr className="lt2-balance-row">
                  <td colSpan={3} className="lt2-td lt2-td-balance tt-label">
                    BALANCE
                  </td>
                  <td className="lt2-td lt2-td-balance">
                    <BalanceCell value={balance.weight} suffix=" g" />
                  </td>
                  <td className="lt2-td lt2-td-balance">
                    <BalanceCell value={balance.rate} />
                  </td>
                  <td className="lt2-td lt2-td-balance">—</td>
                  <td className="lt2-td lt2-td-balance">
                    <BalanceCell value={balance.amount} />
                  </td>
                  <td
                    colSpan={showActions ? 5 : 4}
                    className="lt2-td lt2-td-balance"
                  />
                  <td className="lt2-divider-col" />
                  <td
                    colSpan={actColCount}
                    className="lt2-td lt2-td-balance-gv"
                  />
                </tr>
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
   DATA CELLS
───────────────────────────────────────────── */
function DataCells({ row, sno, side = "rx" }) {
  const statusStyle = STATUS_CLASS[row.status?.toLowerCase()] || {
    bg: "#f1f5f9",
    color: "#475569",
  };

  return (
    <>
      <td className={`lt2-td lt2-td-${side}`}>
        <span className="lt2-serial">{sno}</span>
      </td>
      <td className={`lt2-td lt2-td-${side}`}>
        <span className="lt2-party">{row.partyName || "—"}</span>
      </td>
      <td className={`lt2-td lt2-td-${side} tt-desc`}>
        {row.description || "—"}
      </td>
      <td className={`lt2-td lt2-td-${side}`}>{showValue(row.weight)}</td>
      <td className={`lt2-td lt2-td-${side}`}>
        {getAmountInCurrencyFormat(showValue(row.rate))}
      </td>
      <td className={`lt2-td lt2-td-${side}`}>{showValue(row.diff)}</td>
      <td className={`lt2-td lt2-td-${side}`}>
        {getAmountInCurrencyFormat(showValue(row.amount))}
      </td>
      <td className={`lt2-td lt2-td-${side}`}>
        <span
          className="tt-status"
          style={{ background: statusStyle.bg, color: statusStyle.color }}
        >
          {row.status || "—"}
        </span>
      </td>
      <td className={`lt2-td lt2-td-${side}`}>{fmt(row.postDate)}</td>
      <td className={`lt2-td lt2-td-${side}`}>{fmt(row.date)}</td>
      <td className={`lt2-td lt2-td-${side}`}>{row.time || "—"}</td>
    </>
  );
}

/* ─────────────────────────────────────────────
   ACTION CELL
───────────────────────────────────────────── */
function ActionCell({
  item,
  rescheduleId,
  newDate,
  setRescheduleId,
  setNewDate,
  handleAction,
}) {
  if (item.isCompleted) {
    return (
      <span className="tt-done">
        {/* <i className="fas fa-check-circle" /> */}
        <button
          className="lt2-btn lt2-btn-remove"
          title="Delete Txn"
          onClick={() =>
            handleAction(item.transactionId, {
              action: "deleteTx",
              section: item.section,
              type: item.type,
            })
          }
        >
          <i className="fas fa-trash fa-xs" />
        </button>
      </span>
    );
  }

  if (rescheduleId === item.transactionId) {
    return (
      <div className="tt-reschedule">
        <input
          type="date"
          className="tt-date-input"
          value={newDate}
          onChange={(e) => setNewDate(e.target.value)}
        />
        <button
          className="lt2-btn lt2-btn-edit"
          title="Confirm"
          onClick={() =>
            handleAction(item.transactionId, {
              action: "reschedule",
              date: newDate,
              section: item.section,
              type: item.type,
            })
          }
        >
          <i className="fas fa-check fa-xs" />
        </button>
        <button
          className="lt2-btn lt2-btn-remove"
          title="Cancel"
          onClick={() => {
            setRescheduleId(null);
            setNewDate("");
          }}
        >
          <i className="fas fa-times fa-xs" />
        </button>
      </div>
    );
  }

  return (
    <div className="lt2-actions">
      <button
        className="lt2-btn lt2-btn-edit"
        title="Complete"
        onClick={() =>
          handleAction(item.transactionId, {
            action: "complete",
            section: item.section,
            type: item.type,
          })
        }
      >
        <i className="fas fa-check fa-xs" />
      </button>
      <button
        className="lt2-btn lt2-btn-remove"
        title="Reject"
        onClick={() =>
          handleAction(item.transactionId, {
            action: "reject",
            section: item.section,
            type: item.type,
          })
        }
      >
        <i className="fas fa-times fa-xs" />
      </button>
      <button
        className="lt2-btn lt2-btn-revert"
        title="Reschedule"
        onClick={() => setRescheduleId(item.transactionId)}
      >
        <i className="fas fa-rotate-left fa-xs" />
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   BALANCE CELL  (color-coded +/-)
───────────────────────────────────────────── */
function BalanceCell({ value, suffix = "" }) {
  if (!value && value !== 0) return <span>—</span>;
  const isPos = value >= 0;
  return (
    <span className={`tt-bal-val ${isPos ? "tt-bal-pos" : "tt-bal-neg"}`}>
      <i
        className={`fas ${isPos ? "fa-arrow-trend-up" : "fa-arrow-trend-down"} fa-xs`}
      />
      {getAmountInCurrencyFormat(Math.abs(value))}
      {suffix}
    </span>
  );
}

/* ─────────────────────────────────────────────
   STAT CARD (reuses lt2 classes from CSS)
───────────────────────────────────────────── */
function StatCard({ label, value, icon, color, direction }) {
  return (
    <div className={`lt2-stat-card lt2-stat-${color}`}>
      <div className="lt2-stat-icon">
        <i className={`fas ${icon}`} />
      </div>
      <div className="lt2-stat-body">
        <span className="lt2-stat-label">{label}</span>
        <span className="lt2-stat-value">{value}</span>
        {direction && (
          <span className={`lt2-stat-dir lt2-dir-${direction}`}>
            <i
              className={`fas ${direction === "receivable" ? "fa-arrow-trend-up" : "fa-arrow-trend-down"}`}
            />
            {direction}
          </span>
        )}
      </div>
    </div>
  );
}
